import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Moment, tz } from 'moment-timezone'
import HistoryIcon from '@mui/icons-material/History'
import styled from 'styled-components'
import { Button, ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import Spinner from 'components/Spinner'
import { Panel } from 'components/Panel'

import AlertsDayHistory from 'pages/Device/AlertsPanel/AlertsDayHistory'

import useAlertsHistory from 'hooks/useAlertsHistory'

import { shortDateWithDayOfWeek } from 'helpers/formatters'
import { updateAlertsSeverity } from 'helpers/alerts'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import { Alert } from 'types/Alert'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

export type Props = {
  dealerId: number
  deviceId: string
  deviceTimeZone: string
  showDayRthCallback: (day: Moment) => void
}

const AlertsHistory = React.memo(({ dealerId, deviceId, deviceTimeZone, showDayRthCallback }: Props) => {
  const alertHistoryToTime = useMemo(() => tz(deviceTimeZone).endOf('day'), [deviceTimeZone])
  const [alertHistoryFromTime, setAlertHistoryFromTime] = useState(alertHistoryToTime.clone().subtract(7, 'days').startOf('day'))
  const [alerts, setAlerts] = useState<Array<Alert>>([])
  const [loadingState, setLoadingState] = useState({ isLoading: true, showSpinner: false })

  const { alertsHistory, areAlertsHistoryLoaded } = useAlertsHistory({
    dealerId,
    deviceId,
    fromTime: alertHistoryFromTime.format(),
    toTime: alertHistoryToTime.format(),
    deviceTimeZone
  })

  useEffect(() => {
    if (areAlertsHistoryLoaded) {
      setAlerts(updateAlertsSeverity(alertsHistory?.historyDetails) ?? [])
      setLoadingState({ isLoading: false, showSpinner: false })
    }
  }, [alertsHistory?.historyDetails, areAlertsHistoryLoaded])

  const onClickOnShowMore = useCallback(() => {
    setLoadingState({ isLoading: true, showSpinner: true })
    setAlertHistoryFromTime((prev) => prev.clone().subtract(7, 'days'))
  }, [])

  const groupAlertsByDate = useCallback((alerts: Array<Alert>) => {
    return alerts.reduce((acc, alert: Alert) => {
      const dateKey = tz(Number(alert.occurredAt) * 1000, alert.timeZone).startOf('day').valueOf()

      if (!acc[dateKey]) acc[dateKey] = []
      acc[dateKey].push(alert)
      return acc
    }, {} as Record<number, Array<Alert>>)
  }, [])

  const groupedAlerts = useMemo(() => groupAlertsByDate(alerts), [alerts, groupAlertsByDate])

  return (
    <S.AlertHistory>
      <S.Header>
        <HistoryIcon />
        <S.PanelTitle>Alert History</S.PanelTitle>
      </S.Header>
      {loadingState.isLoading ? (
        <Spinner variant="page" data-testid="spinner" />
      ) : (
        <S.AlertsHistoryContent>
          {
            alerts.length > 0 ? (
              <>
                <S.AlertsDateRange>
                  Alerts occurring between {shortDateWithDayOfWeek(alertHistoryFromTime)} and {shortDateWithDayOfWeek(alertHistoryToTime)}
                </S.AlertsDateRange>
                <S.AlertDaysHistory>
                  {Object.entries(groupedAlerts).map(([key, value]) => (
                    <AlertsDayHistory key={key} alerts={value} timeZone={deviceTimeZone} occurredDay={Number(key)} showDayRthCallback={showDayRthCallback} />
                  ))}
                </S.AlertDaysHistory>
              </>
            ) : (
              <div>No Alerts In The Last {alertHistoryToTime.diff(alertHistoryFromTime, 'days')} Days</div>
            )
          }
          <S.ShowMoreHistory>
            <Button decoration="secondary" enabled={alertsHistory?.moreHistory ?? true} onPress={onClickOnShowMore} text="Show More" showSpinner={loadingState.showSpinner} styles={showMoreButtonStyle} />
          </S.ShowMoreHistory>
        </S.AlertsHistoryContent>
      )}
    </S.AlertHistory>
  )
})

export default AlertsHistory

const showMoreButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  text: {
    textTransform: 'capitalize'
  }
}

const S = {
  AlertHistory: styled.div`
    width: 70%;
    margin: 0;
    @media ${screenSize.small} {
      width: 100%;
    }`,
  Header: styled(Panel.Header)`
    flex-direction: row;
    padding-right: 0;
  `,
  PanelTitle: styled(Panel.Title)`
    font-size: 16px;
    text-decoration: underline;
  `,
  AlertsHistoryContent: styled.div`
    padding: 0px 25px;
  `,
  AlertsDateRange: styled.div`
    padding-bottom: 10px;
  `,
  AlertDaysHistory: styled.div`
    & > :nth-child(n) {
      border-top: 1px solid ${color('divider')};
    }

    & > :last-child {
      border-bottom: 1px solid ${color('divider')};
    }
  `,
  ShowMoreHistory: styled.div`
    margin-top: 25px;
  `
}
